import {Component, computed, effect, input, linkedSignal, OnInit, output, signal} from '@angular/core';
import {ElciaButton} from '../buttons/button/button';
import {Menu} from 'primeng/menu';
import {TranslatePipe} from '@ngx-translate/core';

export enum PeriodType {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
}

export interface PeriodRange {
  startDate: Date;
  endDate: Date;
}

export interface PeriodTypeOption {
  value: PeriodType;
  label: string;
}

@Component({
  selector: 'elcia-period-navigator',
  imports: [
    ElciaButton,
    Menu,
    TranslatePipe,
  ],
  templateUrl: './period-navigator.html',
})
export class PeriodNavigatorComponent implements OnInit {
  periodTypes = input<PeriodType[]>([PeriodType.Month]);
  initialDate = input<Date>(new Date());

  currentDate = signal<Date>(new Date());
  currentType = linkedSignal<PeriodType>(() => this.periodTypes()[0] ?? PeriodType.Month);

  private readonly labelKeys: Record<PeriodType, string> = {
    [PeriodType.Day]: 'entity.period-day',
    [PeriodType.Week]: 'entity.period-week',
    [PeriodType.Month]: 'entity.period-month',
    [PeriodType.Quarter]: 'entity.period-quarter',
    [PeriodType.Year]: 'entity.period-year',
  };

  menuItems = computed<PeriodTypeOption[]>(() =>
    this.periodTypes().map((value) => ({value, label: this.labelKeys[value]})),
  );

  label = computed(() => {
    const date = this.currentDate();
    const type = this.currentType();

    switch (type) {
      case PeriodType.Day:
        return new Intl.DateTimeFormat('fr-BE', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(date);

      case PeriodType.Week: {
        const {week, year} = this._isoWeek(date);
        return `S${week} ${year}`;
      }

      case PeriodType.Month:
        return new Intl.DateTimeFormat('fr-BE', {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(date);

      case PeriodType.Quarter:
        return `T${this._quarter(date)} ${date.getUTCFullYear()}`;

      case PeriodType.Year:
        return new Intl.DateTimeFormat('fr-BE', {
          year: 'numeric',
          timeZone: 'UTC',
        }).format(date);
    }
  });

  startDate = computed<Date>(() => {
    const date = this.currentDate();
    const type = this.currentType();

    switch (type) {
      case PeriodType.Day:
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

      case PeriodType.Week:
        return this._weekStart(date);

      case PeriodType.Month:
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

      case PeriodType.Quarter:
        return new Date(Date.UTC(date.getUTCFullYear(), this._quarterStartMonth(date), 1));

      case PeriodType.Year:
        return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    }
  });

  endDate = computed<Date>(() => {
    const date = this.currentDate();
    const type = this.currentType();

    switch (type) {
      case PeriodType.Day:
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));

      case PeriodType.Week: {
        const start = this._weekStart(date);
        return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + 6, 23, 59, 59, 999));
      }

      case PeriodType.Month:
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 23, 59, 59, 999));

      case PeriodType.Quarter:
        return new Date(Date.UTC(date.getUTCFullYear(), this._quarterStartMonth(date) + 3, 0, 23, 59, 59, 999));

      case PeriodType.Year:
        return new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
    }
  });

  periodChange = output<PeriodRange>();

  constructor() {
    let initialized = false;
    effect(() => {
      const start = this.startDate();
      const end = this.endDate();
      if (!initialized) {
        initialized = true;
        return;
      }
      this.periodChange.emit({startDate: start, endDate: end});
    });
  }

  ngOnInit() {
    this.currentDate.set(this.initialDate());
    this.periodChange.emit({startDate: this.startDate(), endDate: this.endDate()});
  }

  selectType(type: PeriodType): void {
    this.currentType.set(type);
  }

  previous(): void {
    this.currentDate.update((date) => this._shift(date, -1));
  }

  next(): void {
    this.currentDate.update((date) => this._shift(date, 1));
  }

  private _weekStart(date: Date): Date {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    // getUTCDay() returns 0 for Sunday: map it to 7 so the ISO week runs Monday → Sunday
    const dayOfWeek = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() - (dayOfWeek - 1));
    return d;
  }

  private _isoWeek(date: Date): {week: number; year: number} {
    // The ISO year is the one holding the week's Thursday: a week straddling two years
    // belongs to the year containing that Thursday (e.g. 29/12/2025 → W1 2026)
    const thursday = this._weekStart(date);
    thursday.setUTCDate(thursday.getUTCDate() + 3);

    const year = thursday.getUTCFullYear();
    const yearStart = Date.UTC(year, 0, 1);
    const week = Math.ceil(((thursday.getTime() - yearStart) / 86_400_000 + 1) / 7);
    return {week, year};
  }

  private _quarter(date: Date): number {
    return Math.floor(date.getUTCMonth() / 3) + 1;
  }

  private _quarterStartMonth(date: Date): number {
    return Math.floor(date.getUTCMonth() / 3) * 3;
  }

  private _shift(date: Date, direction: 1 | -1): Date {
    const d = new Date(date);
    switch (this.currentType()) {
      case PeriodType.Day:
        d.setUTCDate(d.getUTCDate() + direction);
        break;
      case PeriodType.Week:
        d.setUTCDate(d.getUTCDate() + direction * 7);
        break;
      case PeriodType.Month:
        // Mettre le jour à 1 avant de changer le mois évite le débordement
        // (ex: 31 jan + 1 mois → setMonth(1) sur jour 31 → déborde sur mars)
        d.setUTCDate(1);
        d.setUTCMonth(d.getUTCMonth() + direction);
        break;
      case PeriodType.Quarter:
        d.setUTCDate(1);
        d.setUTCMonth(d.getUTCMonth() + direction * 3);
        break;
      case PeriodType.Year:
        d.setUTCFullYear(d.getUTCFullYear() + direction);
        break;
    }
    return d;
  }
}
